import { supabase } from '../lib/supabase';

export const stripeService = {
  /**
   * Create or update Stripe customer and subscription
   */
  async createSubscription(psychologistId, plan, paymentMethodId) {
    try {
      // Call Supabase edge function to create subscription
      const { data, error } = await supabase?.functions?.invoke('create-subscription', {
        body: {
          psychologist_id: psychologistId,
          plan: plan,
          payment_method_id: paymentMethodId
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  },

  /**
   * Cancel subscription at period end
   */
  async cancelSubscription(subscriptionId) {
    try {

      const { data, error } = await supabase?.functions?.invoke('cancel-subscription', {
        body: {
          subscription_id: subscriptionId
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  },

  /**
   * Update subscription plan
   */
  async updateSubscriptionPlan(subscriptionId, newPlan) {
    try {
      const { data, error } = await supabase?.functions?.invoke('update-subscription', {
        body: {
          subscription_id: subscriptionId,
          new_plan: newPlan
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  },

  /**
   * Get subscription details for psychologist
   */
  async getSubscription(psychologistId) {
    try {
      const { data, error } = await supabase
      ?.from('subscriptions')
      ?.select('*')
      ?.eq('psychologist_id', psychologistId)
      ?.maybeSingle();

      if (error) throw error;

      if (!data) {
        console.log("No rows returned");
        return null;
      }

      return data;

    } catch (error) {
      console.error('Error fetching subscription:', error);
      throw error;
    }
  },

  /**
   * Get payment transactions for psychologist
   */
  async getPaymentTransactions(psychologistId) {
    try {
      const { data, error } = await supabase?.from('payment_transactions')?.select('*')?.eq('psychologist_id', psychologistId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching payment transactions:', error);
      throw error;
    }
  },

  /**
   * Check if psychologist has active subscription
   */
  async hasActiveSubscription(psychologistId) {
    try {
      const subscription = await this.getSubscription(psychologistId);
      
      if (!subscription) return false;
      
      // Check if trial is active
      if (subscription?.status === 'trialing') {
        return new Date(subscription.trial_end) > new Date();
      }
      
      // Check if paid subscription is active
      if (subscription?.status === 'active') {
        return !subscription?.current_period_end || new Date(subscription.current_period_end) > new Date();
      }
      
      return false;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  },

  /**
   * Get days remaining in trial/subscription
   */
  getDaysRemaining(subscription) {
    if (!subscription) return 0;
    
    let endDate;
    if (subscription?.status === 'trialing') {
      endDate = new Date(subscription.trial_end);
    } else if (subscription?.status === 'active') {
      endDate = new Date(subscription.current_period_end);
    } else {
      return 0;
    }
    
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? diffDays : 0;
  },

  /**
   * Format amount for display
   */
  formatAmount(amount, currency = 'BRL') {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency,
    })?.format(amount);
  },

  /**
   * Get plan details
   */
  getPlanDetails(plan) {
    const plans = {
      basic: {
        name: 'Básico',
        price: 49.90,
        features: [
          'Agenda de consultas',
          'Gestão de pacientes',
          'Notificações por WhatsApp',
          'Suporte por email',
          ''
        ]
      },
      advanced: {
        name: 'Avançado',
        price: 99.90,
        features: [
          'Todos os recursos Básicos',
          'Página pública para agendamentos',
          'Relatórios avançados',
          'Integração com calendário',
          'Suporte prioritário'
        ]
      },
      pro: {
        name: 'Pro',
        price: 129.90,
        features: [
          'Todos os recursos Avançados',
          'Videochamadas integradas',
          'Prontuário eletrônico completo',
          'API para integrações',
          'Suporte 24/7'
        ]
      }
    };
    
    return plans?.[plan] || plans?.basic;
  },

  /**
   * Calculate prorated refund for current billing period
   */
  calculateProratedRefund(subscription) {
    if (!subscription?.current_period_start || !subscription?.current_period_end) {
      return 0;
    }

    const now = new Date();
    const periodStart = new Date(subscription?.current_period_start);
    const periodEnd = new Date(subscription?.current_period_end);
    
    const totalDays = Math.ceil((periodEnd - periodStart) / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.ceil((periodEnd - now) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining <= 0) return 0;
    
    const dailyRate = subscription?.price_amount / totalDays;
    const refundAmount = dailyRate * daysRemaining;
    
    return Math.max(0, refundAmount);
  },

  /**
   * Get summary of what happens when subscription is cancelled
   */
  getCancellationImpact(subscription) {
    if (!subscription) return null;

    const daysRemaining = this.getDaysRemaining(subscription);
    const proratedRefund = this.calculateProratedRefund(subscription);
    let endDate = new Date(subscription?.current_period_end);

    return {
      accessUntil: endDate?.toLocaleDateString('pt-BR'),
      daysRemaining,
      refundAmount: proratedRefund,
      formattedRefund: this.formatAmount(proratedRefund),
      willBeCharged: false,
      dataRetentionDays: 90
    };
  }
};